import React, { useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import {
  Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Stack, Pagination, Chip, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, LinearProgress, TableSortLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

import { toBRDate, toBRDateTime } from '../utils/date';


export default function SenhasList() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);    // sua API devolve per_page; default 20
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [delId, setDelId] = useState(null);
  const [sortBy, setSortBy] = useState('id_senha');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  const fetchData = async (p = page, sortField = sortBy, sortDirection = sortOrder) => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/api/api/senhas', {
        params: {
          page: p,
          per_page: perPage,
          sort_by: sortField,
          sort_order: sortDirection
        }
      });

      // --- resposta da API ---
      // { current_page, data:[], last_page, per_page, total, ... }
      setRows(res?.data ?? []);
      setPage(res?.current_page ?? p);
      setPerPage(res?.per_page ?? perPage);
      setLastPage(res?.last_page ?? 1);
      setTotal(res?.total ?? 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, sortBy, sortOrder);
    /* eslint-disable-next-line */
  }, []);

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
    fetchData(1, field, newOrder);
  };

  const onChangePage = (_e, val) => {
    setPage(val);
    fetchData(val, sortBy, sortOrder);
  };

  const confirmDelete = async () => {
    if (!delId) return;
    await api.delete(`/api/api/senhas/${delId}`);
    setDelId(null);
    // se apagar o último da página, tenta voltar uma página
    const newPage = (rows.length === 1 && page > 1) ? page - 1 : page;
    fetchData(newPage, sortBy, sortOrder);
  };

  return (
    <AppLayout>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Senhas Cadastradas</Typography>
        <Button variant="contained" onClick={() => navigate('/senhas/novo')}>Cadastrar</Button>
      </Stack>

      <Paper sx={{ position: 'relative' }}>
        {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Categoria</TableCell>
                <TableCell sortDirection={sortBy === 'apelido_senha' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'apelido_senha'}
                    direction={sortBy === 'apelido_senha' ? sortOrder : 'asc'}
                    onClick={() => handleSort('apelido_senha')}
                  >
                    Apelido
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'data_senha' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'data_senha'}
                    direction={sortBy === 'data_senha' ? sortOrder : 'asc'}
                    onClick={() => handleSort('data_senha')}
                  >
                    Cadastro
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'ultimo_acesso' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'ultimo_acesso'}
                    direction={sortBy === 'ultimo_acesso' ? sortOrder : 'asc'}
                    onClick={() => handleSort('ultimo_acesso')}
                  >
                    Último Acesso
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'visita_senha' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'visita_senha'}
                    direction={sortBy === 'visita_senha' ? sortOrder : 'asc'}
                    onClick={() => handleSort('visita_senha')}
                  >
                    Acessos
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'decode_count' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'decode_count'}
                    direction={sortBy === 'decode_count' ? sortOrder : 'asc'}
                    onClick={() => handleSort('decode_count')}
                  >
                    Solicitações de senha
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id_senha} hover>
                  <TableCell>
                    {Array.isArray(r.cat_senha)
                      ? r.cat_senha.map((c, i) => <Chip key={i} label={c} size="small" sx={{ mr: .5 }} />)
                      : r.cat_senha}
                  </TableCell>
                  <TableCell><b>{r.apelido_senha}</b></TableCell>
                  <TableCell>{toBRDate(r.data_senha)}</TableCell>
                  <TableCell>{r.datemodify ? toBRDateTime(`${r.datemodify} ${r.timemodify||'00:00:00'}`) : '-'}</TableCell>
                  <TableCell>{r.visita_senha ?? 0}</TableCell>
                  <TableCell>{r.decode_count ?? 0}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => navigate(`/senhas/${r.id_senha}`)}>Detalhes/Alterar</Button>
                      <Button size="small" color="error" onClick={() => setDelId(r.id_senha)}>Excluir</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7}>Nenhum registro.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Stack alignItems="center" mt={2}>
        <Pagination count={lastPage} page={page} onChange={onChangePage} />
        <Typography variant="caption" sx={{ mt: .5 }}>
          Página {page} de {lastPage} • Total {total}
        </Typography>
      </Stack>

      <Dialog open={!!delId} onClose={() => setDelId(null)}>
        <DialogTitle>Excluir registro?</DialogTitle>
        <DialogContent>
          <DialogContentText>Esta ação não pode ser desfeita.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelId(null)}>Cancelar</Button>
          <Button color="error" onClick={confirmDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
